<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { voiceCallsAPI } from 'dashboard/api/voice/inboxConfig';
import VoiceCallSummaryCard from './VoiceCallSummaryCard.vue';

// ConversationVoiceCallsBanner — see docs/design/ai_voice_platform.md §10.2.
//
// Renders inside MessagesView, between the existing "can't reply" banners
// and the MessageList. Fetches voice_calls scoped to the current
// conversation (Api::V1::Accounts::Conversations::VoiceCallsController);
// if the conversation has none, the component renders nothing — no empty
// state inside a chat thread.
//
// Collapsed by default to avoid pushing the first message off-screen.
// Expanded view re-uses VoiceCallSummaryCard so re-summarize /
// re-transcribe / action-item toggle / audio player all keep working
// without duplicating UI.

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  accountId: { type: [Number, String], required: true },
});

const store = useStore();
const { t } = useI18n();

const calls = ref([]);
const isLoading = ref(false);
const loadError = ref(null);
const isExpanded = ref(false);

const hasCalls = computed(() => calls.value.length > 0);
const callCount = computed(() => calls.value.length);

const sentimentChip = call => {
  if (!call.sentiment) return null;
  const emoji =
    { positive: '🙂', neutral: '😐', negative: '🙁', mixed: '🤔' }[
      call.sentiment
    ] || '😐';
  return `${emoji} ${call.sentiment}`;
};

const formatDuration = seconds => {
  const s = Number(seconds) || 0;
  const mins = Math.floor(s / 60);
  return `${mins}m ${String(s % 60).padStart(2, '0')}s`;
};

const formatTime = iso => (iso ? new Date(iso).toLocaleString() : '—');

const fetchCalls = async () => {
  if (!props.conversationId || !props.accountId) return;
  isLoading.value = true;
  loadError.value = null;
  try {
    const { data } = await voiceCallsAPI.listForConversation(
      props.accountId,
      props.conversationId
    );
    calls.value = data.calls || [];
  } catch (err) {
    loadError.value =
      err?.response?.data?.error ||
      err?.message ||
      t('CONVERSATION_VOICE_BANNER.LOAD_ERROR');
    calls.value = [];
  } finally {
    isLoading.value = false;
  }
};

const toggleExpanded = () => {
  if (!hasCalls.value) return;
  isExpanded.value = !isExpanded.value;
};

onMounted(fetchCalls);
watch(() => [props.conversationId, props.accountId], fetchCalls);
</script>

<template>
  <div
    v-if="hasCalls"
    class="mx-2 mt-2 rounded-lg border border-emerald-200 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-950/30"
  >
    <button
      type="button"
      class="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
      @click="toggleExpanded"
    >
      <div class="flex items-center gap-2 text-sm">
        <span class="text-base">📞</span>
        <span class="font-medium text-emerald-900 dark:text-emerald-100">
          {{ t('CONVERSATION_VOICE_BANNER.TITLE', { count: callCount }) }}
        </span>
        <span class="text-xs text-emerald-700/80 dark:text-emerald-300/80">
          {{
            t('CONVERSATION_VOICE_BANNER.LATEST', {
              time: formatTime(calls[0]?.started_at),
            })
          }}
        </span>
      </div>
      <span class="text-xs text-emerald-700 dark:text-emerald-300">
        {{
          isExpanded
            ? t('CONVERSATION_VOICE_BANNER.COLLAPSE')
            : t('CONVERSATION_VOICE_BANNER.EXPAND')
        }}
        {{ isExpanded ? '▴' : '▾' }}
      </span>
    </button>

    <!-- Collapsed: one-line preview per call -->
    <ul
      v-if="!isExpanded"
      class="flex flex-col gap-1 border-t border-emerald-200 px-3 py-2 dark:border-emerald-900"
    >
      <li
        v-for="call in calls"
        :key="call.id"
        class="flex flex-wrap items-center gap-2 text-xs text-slate-700 dark:text-slate-300"
      >
        <span class="font-medium">{{ formatTime(call.started_at) }}</span>
        <span class="text-slate-400">·</span>
        <span class="capitalize">{{ call.direction }}</span>
        <span class="text-slate-400">·</span>
        <span>{{ formatDuration(call.duration_seconds) }}</span>
        <span v-if="sentimentChip(call)" class="text-slate-400">·</span>
        <span v-if="sentimentChip(call)">{{ sentimentChip(call) }}</span>
        <span
          v-if="call.qa_score != null"
          class="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100"
        >
          QA {{ call.qa_score }}
        </span>
        <span
          v-if="call.summary_preview"
          class="ml-1 flex-1 truncate italic text-slate-500"
        >
          “{{ call.summary_preview }}”
        </span>
      </li>
    </ul>

    <!-- Expanded: full VoiceCallSummaryCard per call. Re-using the card
         keeps audio + transcript + re-summarize/re-transcribe behaviour
         identical to the /voice/calls/:id detail page. -->
    <div
      v-else
      class="flex flex-col gap-3 border-t border-emerald-200 p-3 dark:border-emerald-900"
    >
      <VoiceCallSummaryCard
        v-for="call in calls"
        :key="call.id"
        :call-id="call.id"
      />
    </div>
  </div>

  <!-- Silent on no-calls + no-error; the banner is opt-in by content. -->
  <div
    v-else-if="loadError"
    class="mx-2 mt-2 rounded-lg border border-ruby-200 bg-ruby-50 px-3 py-2 text-xs text-ruby-700"
  >
    {{ loadError }}
  </div>
</template>
